import { useState } from "react";
import { api } from "../api";
import { auth, useAuth } from "../lib/auth";
import { isGrabitRoute, isUsRoute, linkHandler, navigate, type Route } from "../lib/router";

type Section = "markets" | "trades" | "journal" | "backtest" | "settings" | null;

const SECTIONS: { id: Exclude<Section, null>; to: string; label: string }[] = [
  { id: "markets", to: "/ru", label: "Рынки" },
  { id: "trades", to: "/ru/trades", label: "Активные сделки" },
  { id: "journal", to: "/ru/journal", label: "Журнал" },
  { id: "backtest", to: "/ru/backtest", label: "Бэктест" },
  { id: "settings", to: "/settings", label: "Настройки" },
];

/** Раздел США: рынки, сделки и бэктест по своим правилам (отдельно от Мосбиржи). */
const US_SECTIONS: typeof SECTIONS = [
  { id: "markets", to: "/us", label: "Рынки США" },
  { id: "trades", to: "/us/trades", label: "Сделки США" },
  { id: "backtest", to: "/us/backtest", label: "Бэктест США" },
];

/** Grabit MOEX — одна страница: прогноз ДеМарка и позиции FUTOI. */
const GRABIT_SECTIONS: typeof SECTIONS = [{ id: "markets", to: "/grabit", label: "DeMark · FUTOI" }];

function sectionOf(route: Route): Section {
  switch (route.name) {
    case "scanner":
    case "market":
    case "us":
    case "usMarket":
    case "grabit":
      return "markets";
    case "trades":
    case "usTrades":
      return "trades";
    case "journal":
      return "journal";
    case "backtest":
    case "usBacktest":
      return "backtest";
    case "settings":
      return "settings";
    default:
      return null;
  }
}

export function TopBar({ route }: { route: Route }) {
  const me = useAuth();
  const region = isGrabitRoute(route) ? "GRABIT" : isUsRoute(route) ? "US" : "RU";
  const section = sectionOf(route);
  return (
    <header className="topbar">
      <div className="topbar-inner">
        <a href="/ru" onClick={linkHandler("/ru")} className="brand" aria-label="ВнутриCOT — на главную">
          <BrandMark />
          <span className="brand-name">
            Внутри<b>COT</b>
          </span>
        </a>
        <nav className="main-nav" aria-label="Разделы">
          {(region === "GRABIT" ? GRABIT_SECTIONS : region === "US" ? US_SECTIONS : SECTIONS).map((x) => (
            <a
              key={x.id}
              href={x.to}
              onClick={linkHandler(x.to)}
              className={section === x.id ? "active" : undefined}
              aria-current={section === x.id ? "page" : undefined}
            >
              {x.label}
            </a>
          ))}
        </nav>
        <div className="topbar-end">
          <nav className="regions" aria-label="Рынок">
            <a
              href="/ru"
              onClick={linkHandler("/ru")}
              className={region === "RU" ? "active" : undefined}
              aria-current={region === "RU" ? "true" : undefined}
              title="Мосбиржа"
            >
              RU
            </a>
            <a
              href="/us"
              onClick={linkHandler("/us")}
              className={region === "US" ? "active" : undefined}
              aria-current={region === "US" ? "true" : undefined}
              title="США"
            >
              US
            </a>
            <a
              href="/grabit"
              onClick={linkHandler("/grabit")}
              className={region === "GRABIT" ? "active" : undefined}
              aria-current={region === "GRABIT" ? "true" : undefined}
              title="Grabit MOEX: прогноз ДеМарка и позиции FUTOI"
            >
              DeMark
            </a>
          </nav>
          {me?.authenticated ? (
            <div className="userbox">
              <SubscribeButton />
              <a href="/account" onClick={linkHandler("/account")} className="user-link" title="Аккаунт">
                {me.username}
              </a>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  void auth.logout().then(() => navigate("/ru"));
                }}
              >
                Выйти
              </button>
            </div>
          ) : me ? (
            <a href="/login" onClick={linkHandler("/login")} className="btn btn-primary btn-sm">
              Войти
            </a>
          ) : null}
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}

/**
 * «Уведомления в Телеграм»: одноразовая ссылка на закрытого бота (действует 15 минут). Бот пускает только тех, кто
 * пришёл по ссылке с сайта, то есть вошёл на сайт; учётная запись может быть общей — подписывается каждый свой телеграм.
 */
function SubscribeButton() {
  const [state, setState] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });
  const click = async () => {
    // окно открываем сразу по клику — иначе браузер заблокирует его как всплывающее
    const w = window.open("", "_blank");
    setState({ busy: true, error: null });
    try {
      const l = await api.telegramLink();
      if (w) w.location.href = l.url;
      else window.location.href = l.url;
      setState({ busy: false, error: null });
    } catch (err) {
      w?.close();
      setState({ busy: false, error: err instanceof Error ? err.message : String(err) });
    }
  };
  return (
    <span className="subscribe">
      <button
        type="button"
        className="btn btn-primary btn-sm subscribe-btn"
        disabled={state.busy}
        onClick={() => void click()}
        aria-label="Уведомления в Телеграм"
        title="Каждый день в 06:00 МСК: новые сделки, закрытия и новые сетапы"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M21 4 3 11l6 2.2M21 4l-3.5 16-6.8-5.3M21 4 9 13.2m0 0V19l2.7-3.3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
        <span className="subscribe-label">Уведомления в Телеграм</span>
      </button>
      {state.error && (
        <span className="subscribe-error" role="alert">
          {state.error}
        </span>
      )}
    </span>
  );
}

function ThemeToggle() {
  const toggle = () => {
    const root = document.documentElement;
    const current =
      root.dataset.theme ?? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    const next = current === "dark" ? "light" : "dark";
    root.dataset.theme = next;
    try {
      localStorage.setItem("insidera-theme", next);
    } catch {
      /* хранилище недоступно */
    }
  };
  return (
    <button type="button" className="theme-toggle" onClick={toggle} aria-label="Переключить тему">
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" />
      </svg>
    </button>
  );
}

/**
 * Знак «ВнутриCOT»: синяя плитка со скруглёнными углами, внутри — геометрическая «К».
 * Вертикаль «К» — свеча (рынок), ветви — два расходящихся потока: верхняя (сплошная, с точкой) — крупные игроки,
 * нижняя (приглушённая) — противоположная сторона. Читается с 16 px: только крупные формы, без мелких деталей.
 */
export function BrandMark({ size = 30, className = "brand-mark" }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" className={className}>
      <rect x="1" y="1" width="30" height="30" rx="8" className="brand-tile" />
      <rect x="8.5" y="7.5" width="4" height="17" rx="1.4" className="brand-stem" />
      <path d="M13.5 16.4 L21.8 23.6" className="brand-down" />
      <path d="M13.5 16.4 L21.5 9" className="brand-up" />
      <circle cx="22.6" cy="8.2" r="2.6" className="brand-dot" />
    </svg>
  );
}
